import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import {
  Activity,
  ArrowLeft,
  ArrowUpRight,
  Brain,
  BrainCircuit,
  ChevronDown,
  FileText,
  HeartPulse,
  LogOut,
  Menu,
  Search,
  ShieldCheck,
  Speech,
  X,
} from 'lucide-react'

const sections = [
  {
    title: 'Инсульт и ранняя реабилитация',
    icon: HeartPulse,
    scales: [
      ['NIHSS', 'Шкала инсульта NIH', 'Моторика', Activity, 'nihss'],
      ['mRS', 'Модифицированная шкала Рэнкина', 'Функциональность', HeartPulse, 'mrs'],
      ['Индекс Бартела (ADL)', 'Оценка повседневной активности', 'Самообслуживание', FileText, 'bartel'],
      ['GUSS', 'Скрининг глотания', 'Глотание', Speech, 'guss'],
      ['MAS', 'Шкала спастичности Ашворта', 'Моторика', Activity, 'mas'],
    ],
  },
  {
    title: 'Логопедия и нейропсихология',
    icon: Speech,
    scales: [
      ['ТДА', 'Тест диагностики афазий', 'Речь', Speech, 'tda'],
      ['АТА', 'Аахенский тест афазии', 'Речь', Speech, 'ata'],
      ['Frenchay', 'Оценка дизартрии', 'Речь', Activity, 'frenchay'],
      ['DOSS', 'Шкала дисфагии', 'Глотание', HeartPulse, 'doss'],
      ['Тест рисования часов', 'Когнитивная оценка', 'Когниция', Brain, 'clock'],
    ],
  },
  {
    title: 'Нейродегенеративные заболевания (Деменции)',
    icon: Brain,
    scales: [
      ['MoCA', 'Монреальская шкала', 'Когниция', Brain, 'moca'],
      ['MMSE', 'Краткая шкала оценки', 'Когниция', FileText, 'mmse'],
      ['SDMT — вариант 1', 'Символьно-цифровой тест (устная форма)', 'Скорость', Activity, 'sdmt'],
      ['SDMT — вариант 2', 'Пилотная версия — сравните с вариантом 1', 'Скорость', Activity, 'sdmt_v2'],  
      ['Тест Струпа', 'Избирательное внимание и контроль', 'Внимание', Brain, 'stroop'],
      ['FAB', 'Батарея лобной оценки', 'Когниция', Brain, 'fab'],
      ['GDS', 'Шкала Рейсберга', 'Настроение', Activity, 'gds'],
      ['CDR', 'Шкала клинической деменции', 'Когниция', Brain, 'cdr'],
      ['FAQ', 'Опросник функциональной активности', 'Функциональность', FileText, 'faq'],
      ['Индекс Хачинского', 'Ишемическая шкала', 'Когниция', HeartPulse, 'hachinski'],
    ],
  },
  {
    title: 'Паркинсонизм',
    icon: Activity,
    scales: [
      ['UPDRS', 'Шкала болезни Паркинсона', 'Моторика', Activity, 'updrs'],
      ['Шкала Хен и Яра', 'Стадирование заболевания', 'Моторика', Activity, 'henyahr'],
      ['Шкала Шваба и Ингланда', 'Оценка независимости', 'Функциональность', FileText, 'schwab'],
    ],
  },
  {
    title: 'Склерозы',
    icon: ShieldCheck,
    scales: [
      ['ALSFRS-R', 'Шкала БАС', 'Моторика', Activity, 'alsfrs'],
      ['EDSS', 'Шкала Курцке', 'Функциональность', ShieldCheck, 'edss'],
    ],
  },
]

// Шкалы, реально работающие (есть в question_bank).
// Когда подключаешь новую — просто добавь её id сюда.
const AVAILABLE_SCALES = new Set(['moca', 'mmse', 'nihss', 'sdmt', 'sdmt_v2', 'stroop'])

function Logo() {
  return (
    <a href="/" className="flex items-center gap-2.5" aria-label="Cortivox.ai на главную">
      <span className="flex size-8 items-center justify-center rounded-[10px] bg-primary text-primary-foreground"><BrainCircuit className="size-4" /></span>
      <span className="text-[16px] font-semibold tracking-[-0.04em] text-primary">Cortivox<span className="text-accent">.ai</span></span>
    </a>
  )
}

export default function ScalesPage() {
  const [openSection, setOpenSection] = useState(-1)
  const [menuOpen, setMenuOpen] = useState(false)
  // Выход — тем же механизмом, что у врача: AuthContext → Supabase.
  const { signOut } = useAuth()

  // Выход из профиля: сессию закрывает signOut() (там, где она реально
  // хранится), localStorage чистим от старых ключей, и уходим на главную
  // полной перезагрузкой — приложение показывает выбор входа заново.
  const handleLogout = async () => {
    await signOut()
    try { localStorage.clear() } catch (e) { /* хранилище недоступно — не критично */ }
    window.location.href = '/'
  }

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border/70 bg-background/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 lg:px-8">
          <div className="flex items-center gap-4">
            <Logo />
            <Link to="/" className="hidden sm:flex items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-primary">
              <ArrowLeft className="size-3.5" /> На главную
            </Link>
          </div>
          <nav className="hidden items-center gap-6 text-xs font-medium text-muted-foreground lg:flex" aria-label="Основная навигация">
            <a href="/#doctors" className="transition-colors hover:text-primary">Для Врачей</a>
            <a href="/#patients" className="transition-colors hover:text-primary">Для Пациентов</a>
            <a href="/scales" className="text-primary">Шкалы</a>
            <Link to="/exercises" className="transition-colors hover:text-primary">Тренажер</Link>
            <a href="/#about" className="transition-colors hover:text-primary">О Платформе</a>
          </nav>
          <a href="mailto:hello@cortivox.ai" className="hidden items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5 sm:flex">Запросить демо <ArrowUpRight className="size-3.5" /></a>
          <button type="button" onClick={handleLogout} className="hidden items-center gap-1.5 rounded-full border border-border bg-background px-4 py-2.5 text-xs font-semibold text-muted-foreground transition-colors hover:border-red-500/40 hover:text-red-500 sm:flex">Выйти <LogOut className="size-3.5" /></button>
          <button type="button" className="rounded-lg p-2 text-primary lg:hidden" aria-label={menuOpen ? 'Закрыть меню' : 'Открыть меню'} onClick={() => setMenuOpen(!menuOpen)}>{menuOpen ? <X className="size-5" /> : <Menu className="size-5" />}</button>
        </div>
        {menuOpen && <nav className="flex flex-col gap-4 border-t border-border px-5 py-5 text-sm font-medium text-primary lg:hidden" aria-label="Мобильная навигация"><a href="/#doctors">Для Врачей</a><a href="/#patients">Для Пациентов</a><a href="/scales">Шкалы</a><Link to="/exercises">Тренажер</Link><a href="/#about">О Платформе</a><a href="/" className="text-muted-foreground">На главную</a><button type="button" onClick={handleLogout} className="flex items-center gap-1.5 text-left text-red-500"><LogOut className="size-4" /> Выйти</button><a href="mailto:hello@cortivox.ai" className="inline-flex w-fit items-center gap-1.5 rounded-full bg-primary px-4 py-2.5 text-xs font-semibold text-primary-foreground">Запросить демо <ArrowUpRight className="size-3.5" /></a></nav>}
      </header>

      <section className="mx-auto max-w-6xl px-5 pb-4 pt-4 lg:px-8 lg:pt-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <h1 className="text-balance text-2xl font-semibold leading-tight tracking-[-.06em] text-primary sm:text-3xl">База клинических шкал</h1>
          <label className="relative block w-full md:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-accent" />
            <input type="search" placeholder="Поиск шкалы..." aria-label="Поиск шкалы" className="h-11 w-full rounded-xl border border-border bg-background pl-10 pr-4 text-sm text-primary outline-none transition-all placeholder:text-muted-foreground/70 focus:border-accent focus:ring-4 focus:ring-accent/10" />
          </label>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-5 pb-14 lg:px-8">
        <div className="flex flex-col gap-2">
          {sections.map((section, index) => {
            const isOpen = openSection === index
            const SectionIcon = section.icon
            return <div key={section.title} className="overflow-hidden rounded-2xl border border-border bg-background">
              <button type="button" aria-expanded={isOpen} onClick={() => setOpenSection(isOpen ? -1 : index)} className="flex w-full items-center justify-between gap-4 px-5 py-3 text-left transition-colors hover:bg-secondary/40 sm:px-6"><span className="flex min-w-0 items-center gap-3"><span className={`flex size-8 shrink-0 items-center justify-center rounded-xl ${isOpen ? 'bg-primary text-primary-foreground' : 'bg-accent/10 text-accent'}`}><SectionIcon className="size-4" /></span><span><span className="block text-sm font-semibold text-primary sm:text-base">{section.title}</span><span className="mt-0.5 block text-xs text-muted-foreground">{section.scales.length} протоколов</span></span></span><ChevronDown className={`size-4 shrink-0 text-muted-foreground transition-transform duration-300 ${isOpen ? 'rotate-180 text-primary' : ''}`} /></button>
              <div className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}><div className="min-h-0 overflow-hidden"><div className="grid gap-3 border-t border-border bg-secondary/20 p-4 sm:grid-cols-2 sm:p-5 lg:grid-cols-3">
                {section.scales.map(([name, description, tag, Icon, linkId]) => {
  const isAvailable = AVAILABLE_SCALES.has(linkId)
  return (
    <article key={name} className={`group rounded-xl border border-border bg-background p-4 transition-all ${isAvailable ? 'hover:-translate-y-0.5 hover:border-accent/50 hover:shadow-md hover:shadow-primary/5' : 'opacity-70'}`}>
      <div className="flex items-start justify-between gap-3">
        <span className="flex size-8 items-center justify-center rounded-lg bg-accent/10 text-accent"><Icon className="size-4" /></span>
        <span className="rounded-full bg-secondary px-2 py-1 text-[10px] font-medium text-muted-foreground">{tag}</span>
      </div>
      <h3 className={`mt-4 text-sm font-semibold ${isAvailable ? 'text-primary' : 'text-muted-foreground'}`}>{name}</h3>
      <p className="mt-1 min-h-10 text-xs leading-5 text-muted-foreground">{description}</p>
      {isAvailable ? (
        <Link to={`/test/${linkId}`} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-accent transition-colors hover:text-primary">
          Запустить тест <ArrowUpRight className="size-3" />
        </Link>
      ) : (
        <span className="mt-3 inline-flex w-fit items-center rounded-full bg-secondary px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Скоро</span>
      )}
    </article>
  )
})}
              </div></div></div>
            </div>
          })}
        </div>
      </section>

      <footer className="border-t border-border px-5 py-5 lg:px-8"><div className="mx-auto flex max-w-6xl flex-col gap-4 text-xs sm:flex-row sm:items-center sm:justify-between"><Logo /><p className="text-muted-foreground">© 2026 Cortivox.ai</p><div className="flex flex-wrap gap-4 text-muted-foreground"><a href="#" className="transition-colors hover:text-primary">Telegram</a><a href="mailto:hello@cortivox.ai" className="transition-colors hover:text-primary">Email</a><a href="#" className="transition-colors hover:text-primary">Политика конфиденциальности</a></div></div></footer>
    </main>
  )
}